import { createContext, useContext, useMemo } from "react";
import { ChallengeStage } from "@/services/challenges/types/challenges.types";
import { getRealityStages } from "@/services/challenges";
import { useUser } from "./UserContext";
import { useAuthenticatedQuery } from "@/hooks/useAuthenticatedQuery";
import { isBefore } from "date-fns";

type StagesContext = {
  stages: ChallengeStage[];
  currentStage?: ChallengeStage;
  currentStageNumber: number;
  isLoading: boolean;
  isValidStage: boolean;
  isStageClosed: boolean;
  isUserOutOfReality?: boolean;
  finalistIds: number[];
  isSemiFinalist?: boolean;
};

const StagesContext = createContext<StagesContext>({} as StagesContext);

type StagesProviderProps = {
  children: React.ReactNode;
};

export const StagesProvider = ({ children }: StagesProviderProps) => {
  const { userInfo } = useUser();
  const { data: stages, isLoading } = useAuthenticatedQuery(
    ["reality-stages", userInfo?.id.toString()],
    () => getRealityStages(),
    { retry: true }
  );

  const userStages = useMemo(() => {
    if (!stages) {
      return;
    }

    const sortedStages = stages.sort(
      (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
    );

    return sortedStages;
  }, [stages]);

  const currentStage = useMemo(() => {
    if (!userStages) {
      return;
    }

    return userStages[userStages.length - 1];
  }, [userStages]);

  const currentStageNumber = useMemo(() => {
    return userStages?.length ?? -1;
  }, [userStages]);

  const isValidStage = useMemo(() => {
    if (!currentStage) return true;

    return !isBefore(currentStage.endDate, new Date());
  }, [currentStage]);

  const isStageClosed = useMemo(() => {
    return !!currentStage?.isClosed;
  }, [currentStage?.isClosed]);

  //this is only to avoid showing the dialog when the user is a semi finalist
  //TODO: remove this variable
  const isSemiFinalist = useMemo(
    () => !!(userStages?.length && userStages?.length >= 2),
    [userStages?.length]
  );

  const isUserOutOfReality = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    return isStageClosed && !isValidStage && !currentStage?.isFinalStage;
  }, [isLoading, isStageClosed, isValidStage, currentStage?.isFinalStage]);

  return (
    <StagesContext.Provider
      value={{
        stages: stages ?? [],
        currentStage,
        currentStageNumber,
        isValidStage,
        isStageClosed,
        isLoading,
        isUserOutOfReality,
        finalistIds: [],
        isSemiFinalist,
      }}
    >
      {children}
    </StagesContext.Provider>
  );
};

export const useStages = (): StagesContext => useContext(StagesContext);
